import React, { useState, useMemo, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Circle, Ruler, Weight, Percent, Layers, Download, Loader2, Gauge, Hammer } from 'lucide-react';

// ─── Material Database ───────────────────────────────────────────────
// shearStrength (τs) in MPa (N/mm²) — typical values for stamping calculations.
// Source: industry handbooks (ASM, ESHM). Conservative side for press sizing.
const MATERIALS = [
  { name: 'Steel',            density: 7.85,  shearStrength: 320, color: '#6b7280' },
  { name: 'Stainless Steel',  density: 7.93,  shearStrength: 520, color: '#94a3b8' },
  { name: 'Brass',            density: 8.50,  shearStrength: 240, color: '#d4a017' },
  { name: 'Copper',           density: 8.96,  shearStrength: 220, color: '#b87333' },
  { name: 'Aluminium',        density: 2.70,  shearStrength: 150, color: '#a8b8c8' },
];

// ─── html2pdf CDN loader ─────────────────────────────────────────────
const HTML2PDF_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
const loadHtml2Pdf = () =>
  new Promise((resolve, reject) => {
    if (window.html2pdf) return resolve(window.html2pdf);
    const s = document.querySelector(`script[src="${HTML2PDF_CDN}"]`);
    if (s) { s.addEventListener('load', () => resolve(window.html2pdf)); return; }
    const el = document.createElement('script');
    el.src = HTML2PDF_CDN;
    el.onload = () => resolve(window.html2pdf);
    el.onerror = reject;
    document.head.appendChild(el);
  });

// ─── Trigonometric Math Engine ───────────────────────────────────────
//   • Bridge (web between adjacent washers)  : wB = max(1.5, t)
//   • Edge margin (strip side)               : wE = max(2.0, 1.5·wB)
//   • Pitch (centre-to-centre, horizontal)   : p  = OD + wB
//   • Row height (60° staggered nesting)     : h  = p · sin(60°)
//   • Strip width                            : SW = OD + 2·wE + (rows−1)·h
//   • Gross area per piece (honeycomb)       : (SW · p) / rows
//   • Shear force (blank + pierce)           : F = π·(OD+ID) · t · τs  (×1.10 stripping)
function compute(od, id, t, rows, density, shearStrength) {
  if (!od || !id || !t || od <= 0 || id <= 0 || t <= 0 || id >= od) return null;

  // ─── Strip layout ─────────────────────────────────────
  const wBridge = Math.max(1.5, t);                  // web between pieces
  const wEdge   = Math.max(2.0, wBridge * 1.5);      // strip-side edge margin (1.5× safety)

  const p = od + wBridge;                            // horizontal pitch
  const h = p * Math.sin(Math.PI / 3);               // row height = p · sin(60°)
  const stripWidth = od + 2 * wEdge + (rows - 1) * h;

  // ─── Weights ──────────────────────────────────────────
  const netArea = Math.PI * (Math.pow(od / 2, 2) - Math.pow(id / 2, 2)); // mm²
  const netWt   = (netArea * t * density) / 1000;                        // g/piece

  // Gross area per piece accounting for honeycomb + edge waste.
  // Strip area per pitch unit = stripWidth · p; pieces per pitch = rows
  const grossArea = (stripWidth * p) / rows;                             // mm²/piece
  const grossWt   = (grossArea * t * density) / 1000;                    // g/piece
  const scrap     = ((grossWt - netWt) / grossWt) * 100;

  // ─── Press tonnage ────────────────────────────────────
  // Shear force per cut: F = L · t · τs  (Newtons)
  // Per washer: blank (πOD) + pierce (πID) = π(OD+ID)
  // Stripping force ≈ +10% (to push the part off the punch).
  // 1 metric tonne-force = 9806.65 N
  const perimeter   = Math.PI * (od + id);                  // mm
  const shearForce  = perimeter * t * shearStrength;        // N (per piece)
  const totalForce  = shearForce * 1.10;                    // +10% stripping

  // Compound tool — single-station, blank+pierce in one stroke → 1 piece/stroke.
  const compoundForceN    = totalForce;
  const compoundForceKN   = compoundForceN / 1000;
  const compoundTonnage   = compoundForceN / 9806.65;

  // Progressive tool — multi-row staggered die: stamps `rows` pieces per stroke
  // (one from each cross-strip row, all stations fire simultaneously).
  const progressiveForceN  = totalForce * rows;
  const progressiveForceKN = progressiveForceN / 1000;
  const progressiveTonnage = progressiveForceN / 9806.65;

  return {
    wBridge, wEdge, p, h, stripWidth,
    netWt, grossWt, scrap,
    netPer1000:   netWt,   // grams/piece × 1000 ÷ 1000 = kg/1000pcs
    grossPer1000: grossWt,
    perimeter,
    compoundForceN, compoundForceKN, compoundTonnage,
    progressiveForceN, progressiveForceKN, progressiveTonnage,
  };
}

// ─── SVG Strip Visualizer ────────────────────────────────────────────
// Task 1: SVG Compatibility for html2canvas - explicit xmlns, dimensions, inline styles
function StripVisualizer({ od, id, p, h, wEdge, rows, stripWidth, materialColor, forPdf = false }) {
  const cols = Math.max(4, Math.min(7, Math.ceil(14 / rows)));
  const padX = p * 0.3;
  const padY = wEdge * 0.5;

  // Real-world coordinate space
  const worldW = cols * p + p / 2 + 2 * padX;
  const worldH = stripWidth + 2 * padY;

  // Calculate pixel dimensions for PDF rendering (Task 1: explicit hardcoded dimensions)
  const scale = forPdf ? 12 : 1;  // Scale up for PDF clarity
  const pixelWidth = worldW * scale;
  const pixelHeight = worldH * scale;

  // Build washer positions
  const washers = [];
  for (let r = 0; r < rows; r++) {
    const cy = padY + wEdge + od / 2 + r * h;
    const xOff = r % 2 === 1 ? p / 2 : 0;
    for (let c = 0; c < cols; c++) {
      const cx = padX + od / 2 + wEdge + c * p + xOff;
      washers.push({ cx, cy, r, c });
    }
  }

  // Task 1: Convert materialColor opacity to solid hex for PDF compatibility
  const stripFillColor = forPdf ? hexWithOpacity(materialColor, 0.18) : materialColor;
  const holeFillColor = forPdf ? hexWithOpacity(materialColor, 0.25) : materialColor;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox={`0 0 ${worldW} ${worldH}`}
      width={forPdf ? pixelWidth : undefined}
      height={forPdf ? pixelHeight : undefined}
      className={forPdf ? undefined : "w-full"}
      style={forPdf ? { width: pixelWidth, height: pixelHeight, display: 'block' } : { maxHeight: 420 }}
      data-testid="strip-visualizer-svg"
    >
      <defs>
        <pattern id="grid" width={p} height={h} patternUnits="userSpaceOnUse"
          patternTransform={`translate(${padX + wEdge},${padY})`}>
          <line x1="0" y1="0" x2={p} y2="0" stroke="#e5e7eb" strokeWidth="0.25" />
          <line x1="0" y1="0" x2="0" y2={h} stroke="#e5e7eb" strokeWidth="0.25" />
        </pattern>
      </defs>

      {/* Strip background */}
      <rect
        x={padX} y={padY}
        width={worldW - 2 * padX}
        height={stripWidth}
        rx={1}
        fill={forPdf ? stripFillColor : materialColor}
        opacity={forPdf ? 1 : 0.18}
        stroke={materialColor}
        strokeWidth={0.6}
      />
      <rect
        x={padX} y={padY}
        width={worldW - 2 * padX}
        height={stripWidth}
        fill="url(#grid)"
      />

      {/* Strip width dimension line */}
      <line x1={padX - padX * 0.35} y1={padY} x2={padX - padX * 0.35} y2={padY + stripWidth}
        stroke="#1e40af" strokeWidth={0.4} markerStart="url(#arrowUp)" markerEnd="url(#arrowDown)" />
      <text x={padX - padX * 0.55} y={padY + stripWidth / 2}
        fontSize={Math.min(3.5, stripWidth * 0.07)}
        fill="#1e40af" textAnchor="middle" dominantBaseline="middle"
        transform={`rotate(-90,${padX - padX * 0.55},${padY + stripWidth / 2})`}
        fontWeight="600"
        fontFamily="system-ui, -apple-system, sans-serif">
        Strip {stripWidth.toFixed(1)} mm
      </text>

      {/* Washer blanks */}
      {washers.map((wsh) => (
        <g key={`w-${wsh.r}-${wsh.c}`}>
          {/* Outer circle — blank */}
          <circle cx={wsh.cx} cy={wsh.cy} r={od / 2}
            fill="#ffffff" stroke="#1e40af" strokeWidth={0.5} opacity={forPdf ? 1 : 0.92} />
          {/* Inner hole */}
          <circle cx={wsh.cx} cy={wsh.cy} r={id / 2}
            fill={forPdf ? holeFillColor : materialColor}
            opacity={forPdf ? 1 : 0.25}
            stroke="#475569" strokeWidth={0.35} strokeDasharray="1,0.8" />
        </g>
      ))}

      {/* Pitch dimension (first row, first two washers) */}
      {washers.length >= 2 && (
        <>
          <line x1={washers[0].cx} y1={washers[0].cy - od / 2 - 2}
                x2={washers[1].cx} y2={washers[1].cy - od / 2 - 2}
                stroke="#dc2626" strokeWidth={0.35} />
          <text x={(washers[0].cx + washers[1].cx) / 2} y={washers[0].cy - od / 2 - 3.2}
            fontSize={Math.min(3, p * 0.18)} fill="#dc2626" textAnchor="middle" fontWeight="600"
            fontFamily="system-ui, -apple-system, sans-serif">
            p={p.toFixed(1)}
          </text>
        </>
      )}

      {/* Row height dimension (if multi-row) */}
      {rows > 1 && washers.length > cols && (
        <>
          <line x1={washers[0].cx + od / 2 + 2} y1={washers[0].cy}
                x2={washers[0].cx + od / 2 + 2} y2={washers[0].cy + h}
                stroke="#059669" strokeWidth={0.35} />
          <text x={washers[0].cx + od / 2 + 3.5}
                y={washers[0].cy + h / 2}
                fontSize={Math.min(3, h * 0.18)} fill="#059669" textAnchor="start"
                dominantBaseline="middle" fontWeight="600"
                fontFamily="system-ui, -apple-system, sans-serif">
            h={h.toFixed(1)}
          </text>
        </>
      )}
    </svg>
  );
}

// Helper: Blend hex color with opacity into a solid hex (for PDF compatibility)
function hexWithOpacity(hex, opacity) {
  // Convert hex to RGB, blend with white background, return new hex
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const blendedR = Math.round(r * opacity + 255 * (1 - opacity));
  const blendedG = Math.round(g * opacity + 255 * (1 - opacity));
  const blendedB = Math.round(b * opacity + 255 * (1 - opacity));
  return `#${blendedR.toString(16).padStart(2, '0')}${blendedG.toString(16).padStart(2, '0')}${blendedB.toString(16).padStart(2, '0')}`;
}

// Task 2: SVG to Canvas converter for html2canvas compatibility
async function svgToCanvas(svgElement) {
  return new Promise((resolve, reject) => {
    try {
      // Clone and prepare SVG for serialization
      const clonedSvg = svgElement.cloneNode(true);
      
      // Ensure xmlns is set
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      
      // Get dimensions from viewBox or attributes
      const viewBox = clonedSvg.getAttribute('viewBox');
      let width, height;
      if (viewBox) {
        const parts = viewBox.split(' ').map(Number);
        width = parts[2] * 12;  // Scale up for clarity
        height = parts[3] * 12;
      } else {
        width = parseFloat(clonedSvg.getAttribute('width')) || 800;
        height = parseFloat(clonedSvg.getAttribute('height')) || 400;
      }
      
      // Set explicit dimensions on SVG
      clonedSvg.setAttribute('width', width);
      clonedSvg.setAttribute('height', height);
      
      // Serialize SVG to string
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(clonedSvg);
      
      // Ensure proper XML declaration for Data URI
      if (!svgString.includes('xmlns=')) {
        svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      
      // Create Data URI
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      // Create Image and draw to Canvas
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        
        // Draw SVG
        ctx.drawImage(img, 0, 0, width, height);
        
        URL.revokeObjectURL(url);
        resolve(canvas);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load SVG as image'));
      };
      img.src = url;
    } catch (error) {
      reject(error);
    }
  });
}

// ─── Metric Card ─────────────────────────────────────────────────────
function Metric({ label, value, unit, icon: Icon, border, accent }) {
  return (
    <Card className={`border-l-4 ${border}`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          <Icon className={`h-4 w-4 ${accent}`} />
        </div>
        <p className={`text-2xl font-bold font-mono ${accent}`}>{value}</p>
        {unit && <p className="text-[10px] text-muted-foreground mt-0.5">{unit}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────
export default function WasherCalculatorPage() {
  const [material, setMaterial] = useState('Steel');
  const [od, setOd] = useState(20);
  const [id, setId] = useState(10);
  const [thickness, setThickness] = useState(2);
  const [rows, setRows] = useState(3);
  const [pdfLoading, setPdfLoading] = useState(false);
  const vizRef = useRef(null);

  const mat = MATERIALS.find((m) => m.name === material) || MATERIALS[0];
  const result = useMemo(
    () => compute(od, id, thickness, rows, mat.density, mat.shearStrength),
    [od, id, thickness, rows, mat.density, mat.shearStrength]
  );

  const handleNum = useCallback((setter) => (e) => {
    const v = parseFloat(e.target.value);
    if (!isNaN(v) && v >= 0) setter(v);
    else if (e.target.value === '') setter(0);
  }, []);

  const handleDownloadPdf = async () => {
    if (!result) return;
    setPdfLoading(true);
    try {
      await loadHtml2Pdf();
      
      // Build off-screen container for PDF
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1000px;background:#fff;padding:40px;font-family:system-ui,sans-serif;color:#111;';

      // Task 1 & 2: Generate PDF-optimized SVG and convert to Canvas for html2canvas compatibility
      let visualizerHtml = '';
      try {
        // Create a temporary container to render the PDF-optimized SVG
        const tempDiv = document.createElement('div');
        tempDiv.style.cssText = 'position:absolute;left:-9999px;top:0;';
        document.body.appendChild(tempDiv);
        
        // Render PDF-optimized SVG using ReactDOMServer would be ideal, but we'll build it manually
        // Task 1: Build SVG with explicit xmlns, dimensions, and inline hex colors
        const cols = Math.max(4, Math.min(7, Math.ceil(14 / rows)));
        const padX = result.p * 0.3;
        const padY = result.wEdge * 0.5;
        const worldW = cols * result.p + result.p / 2 + 2 * padX;
        const worldH = result.stripWidth + 2 * padY;
        const scale = 12;
        const pixelWidth = worldW * scale;
        const pixelHeight = worldH * scale;
        
        // Build washer positions
        const washers = [];
        for (let r = 0; r < rows; r++) {
          const cy = padY + result.wEdge + od / 2 + r * result.h;
          const xOff = r % 2 === 1 ? result.p / 2 : 0;
          for (let c = 0; c < cols; c++) {
            const cx = padX + od / 2 + result.wEdge + c * result.p + xOff;
            washers.push({ cx, cy, r, c });
          }
        }
        
        // Task 1: Convert opacity colors to solid hex
        const stripFillColor = hexWithOpacity(mat.color, 0.18);
        const holeFillColor = hexWithOpacity(mat.color, 0.25);
        
        // Build washer circles SVG
        const washersSvg = washers.map((wsh, i) => `
          <circle cx="${wsh.cx}" cy="${wsh.cy}" r="${od / 2}" fill="#ffffff" stroke="#1e40af" stroke-width="0.5"/>
          <circle cx="${wsh.cx}" cy="${wsh.cy}" r="${id / 2}" fill="${holeFillColor}" stroke="#475569" stroke-width="0.35" stroke-dasharray="1,0.8"/>
        `).join('');
        
        // Build pitch dimension if we have 2+ washers
        const pitchDimSvg = washers.length >= 2 ? `
          <line x1="${washers[0].cx}" y1="${washers[0].cy - od / 2 - 2}" x2="${washers[1].cx}" y2="${washers[1].cy - od / 2 - 2}" stroke="#dc2626" stroke-width="0.35"/>
          <text x="${(washers[0].cx + washers[1].cx) / 2}" y="${washers[0].cy - od / 2 - 3.2}" font-size="${Math.min(3, result.p * 0.18)}" fill="#dc2626" text-anchor="middle" font-weight="600" font-family="system-ui, sans-serif">p=${result.p.toFixed(1)}</text>
        ` : '';
        
        // Build row height dimension if multi-row
        const rowHeightSvg = rows > 1 && washers.length > cols ? `
          <line x1="${washers[0].cx + od / 2 + 2}" y1="${washers[0].cy}" x2="${washers[0].cx + od / 2 + 2}" y2="${washers[0].cy + result.h}" stroke="#059669" stroke-width="0.35"/>
          <text x="${washers[0].cx + od / 2 + 3.5}" y="${washers[0].cy + result.h / 2}" font-size="${Math.min(3, result.h * 0.18)}" fill="#059669" text-anchor="start" dominant-baseline="middle" font-weight="600" font-family="system-ui, sans-serif">h=${result.h.toFixed(1)}</text>
        ` : '';
        
        // Task 1: Build complete SVG with xmlns, explicit dimensions, inline styles
        const svgMarkup = `
          <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
               viewBox="0 0 ${worldW} ${worldH}" 
               width="${pixelWidth}" height="${pixelHeight}"
               style="width:${pixelWidth}px;height:${pixelHeight}px;display:block;">
            <defs>
              <pattern id="grid-pdf" width="${result.p}" height="${result.h}" patternUnits="userSpaceOnUse" patternTransform="translate(${padX + result.wEdge},${padY})">
                <line x1="0" y1="0" x2="${result.p}" y2="0" stroke="#e5e7eb" stroke-width="0.25"/>
                <line x1="0" y1="0" x2="0" y2="${result.h}" stroke="#e5e7eb" stroke-width="0.25"/>
              </pattern>
            </defs>
            <rect x="${padX}" y="${padY}" width="${worldW - 2 * padX}" height="${result.stripWidth}" rx="1" fill="${stripFillColor}" stroke="${mat.color}" stroke-width="0.6"/>
            <rect x="${padX}" y="${padY}" width="${worldW - 2 * padX}" height="${result.stripWidth}" fill="url(#grid-pdf)"/>
            <line x1="${padX - padX * 0.35}" y1="${padY}" x2="${padX - padX * 0.35}" y2="${padY + result.stripWidth}" stroke="#1e40af" stroke-width="0.4"/>
            <text x="${padX - padX * 0.55}" y="${padY + result.stripWidth / 2}" font-size="${Math.min(3.5, result.stripWidth * 0.07)}" fill="#1e40af" text-anchor="middle" dominant-baseline="middle" transform="rotate(-90,${padX - padX * 0.55},${padY + result.stripWidth / 2})" font-weight="600" font-family="system-ui, sans-serif">Strip ${result.stripWidth.toFixed(1)} mm</text>
            ${washersSvg}
            ${pitchDimSvg}
            ${rowHeightSvg}
          </svg>
        `;
        
        // Task 2: Convert SVG to Canvas for guaranteed html2canvas compatibility
        const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const canvas = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const cvs = document.createElement('canvas');
            cvs.width = pixelWidth;
            cvs.height = pixelHeight;
            const ctx = cvs.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, pixelWidth, pixelHeight);
            ctx.drawImage(img, 0, 0, pixelWidth, pixelHeight);
            URL.revokeObjectURL(svgUrl);
            resolve(cvs);
          };
          img.onerror = () => {
            URL.revokeObjectURL(svgUrl);
            reject(new Error('SVG to Canvas conversion failed'));
          };
          img.src = svgUrl;
        });
        
        // Convert canvas to base64 image for embedding in HTML
        const canvasDataUrl = canvas.toDataURL('image/png');
        visualizerHtml = `<img src="${canvasDataUrl}" style="width:100%;max-width:${pixelWidth}px;height:auto;display:block;margin:0 auto;" alt="Strip Layout"/>`;
        
        document.body.removeChild(tempDiv);
      } catch (svgError) {
        // Fallback: Use the on-screen SVG as-is (may have rendering issues)
        const svgEl = vizRef.current?.querySelector('svg');
        visualizerHtml = svgEl ? svgEl.outerHTML : '<p style="color:#999;text-align:center;">Visualizer unavailable</p>';
      }

      // SECURITY: content below is sanitized via DOMPurify before being assigned
      // to innerHTML. The string is a static template + numeric/string fields
      // already coerced from typed user inputs (no untrusted HTML). Do NOT remove
      // the DOMPurify.sanitize() wrapper.
      container.innerHTML = DOMPurify.sanitize(`
        <div style="border-bottom:3px solid #1e40af;padding-bottom:16px;margin-bottom:24px;display:flex;justify-content:space-between;align-items:flex-end;">
          <div>
            <h1 style="font-size:28px;font-weight:800;color:#1e40af;margin:0;letter-spacing:-0.5px;">WASHER CALCULATION SHEET</h1>
            <p style="color:#6b7280;font-size:13px;margin:4px 0 0;">Sturdfix &mdash; Singhania International Ltd.</p>
          </div>
          <p style="color:#6b7280;font-size:12px;margin:0;">${new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</p>
        </div>

        <table style="width:100%;border-collapse:collapse;margin-bottom:24px;font-size:14px;">
          <tr style="background:#f1f5f9;">
            <td style="padding:8px 12px;font-weight:600;width:25%;">Material</td>
            <td style="padding:8px 12px;">${material} (${mat.density} g/cm&sup3;)</td>
            <td style="padding:8px 12px;font-weight:600;width:25%;">Staggered Rows</td>
            <td style="padding:8px 12px;">${rows}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;font-weight:600;">OD</td>
            <td style="padding:8px 12px;">${od} mm</td>
            <td style="padding:8px 12px;font-weight:600;">ID</td>
            <td style="padding:8px 12px;">${id} mm</td>
          </tr>
          <tr style="background:#f1f5f9;">
            <td style="padding:8px 12px;font-weight:600;">Thickness (T)</td>
            <td style="padding:8px 12px;">${thickness} mm</td>
            <td style="padding:8px 12px;font-weight:600;">Shear Strength (&tau;s)</td>
            <td style="padding:8px 12px;">${mat.shearStrength} MPa</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;font-weight:600;">Bridge web (w<sub>B</sub>)</td>
            <td style="padding:8px 12px;">${result.wBridge.toFixed(2)} mm <span style="color:#6b7280;font-size:11px;">max(1.5, T)</span></td>
            <td style="padding:8px 12px;font-weight:600;">Edge margin (w<sub>E</sub>)</td>
            <td style="padding:8px 12px;">${result.wEdge.toFixed(2)} mm <span style="color:#6b7280;font-size:11px;">max(2.0, 1.5&middot;w<sub>B</sub>)</span></td>
          </tr>
          <tr style="background:#f1f5f9;">
            <td style="padding:8px 12px;font-weight:600;">Pitch (p)</td>
            <td style="padding:8px 12px;">${result.p.toFixed(2)} mm</td>
            <td style="padding:8px 12px;font-weight:600;">Row Height (h)</td>
            <td style="padding:8px 12px;">${result.h.toFixed(2)} mm</td>
          </tr>
        </table>

        <div style="display:flex;gap:16px;margin-bottom:20px;">
          <div style="flex:1;background:#eff6ff;border-left:4px solid #3b82f6;padding:14px 18px;border-radius:6px;">
            <p style="font-size:11px;color:#6b7280;margin:0 0 4px;text-transform:uppercase;font-weight:600;">Net Weight / 1000 pcs</p>
            <p style="font-size:24px;font-weight:700;color:#2563eb;margin:0;">${result.netPer1000.toFixed(2)} Kg</p>
          </div>
          <div style="flex:1;background:#fffbeb;border-left:4px solid #f59e0b;padding:14px 18px;border-radius:6px;">
            <p style="font-size:11px;color:#6b7280;margin:0 0 4px;text-transform:uppercase;font-weight:600;">Gross Weight / 1000 pcs</p>
            <p style="font-size:24px;font-weight:700;color:#d97706;margin:0;">${result.grossPer1000.toFixed(2)} Kg</p>
          </div>
          <div style="flex:1;background:${result.scrap<50?'#ecfdf5':'#fef2f2'};border-left:4px solid ${result.scrap<50?'#10b981':'#ef4444'};padding:14px 18px;border-radius:6px;">
            <p style="font-size:11px;color:#6b7280;margin:0 0 4px;text-transform:uppercase;font-weight:600;">Scrap %</p>
            <p style="font-size:24px;font-weight:700;color:${result.scrap<50?'#059669':'#dc2626'};margin:0;">${result.scrap.toFixed(1)}%</p>
          </div>
          <div style="flex:1;background:#f5f3ff;border-left:4px solid #8b5cf6;padding:14px 18px;border-radius:6px;">
            <p style="font-size:11px;color:#6b7280;margin:0 0 4px;text-transform:uppercase;font-weight:600;">Strip Width</p>
            <p style="font-size:24px;font-weight:700;color:#7c3aed;margin:0;">${result.stripWidth.toFixed(1)} mm</p>
          </div>
        </div>

        <h3 style="font-size:13px;font-weight:700;color:#1e40af;margin:0 0 8px;text-transform:uppercase;letter-spacing:0.5px;">
          Press Tonnage Required &mdash; F = &pi;&middot;(OD+ID)&middot;t&middot;&tau;s &times; 1.10
        </h3>
        <div style="display:flex;gap:16px;margin-bottom:28px;">
          <div style="flex:1;background:#fef3c7;border:1px solid #f59e0b;padding:14px 18px;border-radius:6px;">
            <p style="font-size:11px;color:#92400e;margin:0 0 4px;text-transform:uppercase;font-weight:700;">Compound Tool &middot; 1 pc/stroke</p>
            <p style="font-size:26px;font-weight:800;color:#b45309;margin:0;">${result.compoundTonnage.toFixed(2)} tonne</p>
            <p style="font-size:11px;color:#6b7280;margin:4px 0 0;">${result.compoundForceKN.toFixed(1)} kN &middot; ${(result.compoundForceN/1000).toFixed(1)} kN</p>
          </div>
          <div style="flex:1;background:#dcfce7;border:1px solid #10b981;padding:14px 18px;border-radius:6px;">
            <p style="font-size:11px;color:#065f46;margin:0 0 4px;text-transform:uppercase;font-weight:700;">Progressive Tool &middot; ${rows} pc/stroke</p>
            <p style="font-size:26px;font-weight:800;color:#047857;margin:0;">${result.progressiveTonnage.toFixed(2)} tonne</p>
            <p style="font-size:11px;color:#6b7280;margin:4px 0 0;">${result.progressiveForceKN.toFixed(1)} kN &middot; total cut perimeter ${(result.perimeter*rows).toFixed(0)} mm</p>
          </div>
        </div>

        <h3 style="font-size:15px;font-weight:700;color:#1e40af;margin:0 0 12px;text-transform:uppercase;letter-spacing:0.5px;">
          Staggered Strip Layout &mdash; ${rows} Row${rows>1?'s':''} &middot; sin(60&deg;) Nesting
        </h3>
        <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:16px;text-align:center;">
          ${visualizerHtml}
        </div>
        <p style="font-size:10px;color:#9ca3af;text-align:center;margin-top:16px;">
          Generated by Sturdfix Engineering Calculator &mdash; ${new Date().toISOString()}
        </p>
      `, { ADD_TAGS: ['style'], ADD_ATTR: ['style'] });
      document.body.appendChild(container);

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      await window.html2pdf().set({
        margin: 10,
        filename: `Washer_Calc_${material}_OD${od}_ID${id}_T${thickness}_R${rows}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
      }).from(container).save();

      document.body.removeChild(container);
    } catch (err) {
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="washer-calculator-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
          Washer Calculator
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Weight & staggered strip layout calculator for stamped washers
        </p>
        {result && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            data-testid="download-pdf-btn"
          >
            {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download Calculation Sheet (PDF)
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ────── Left: Input Form ────── */}
        <div className="lg:col-span-4">
          <Card data-testid="input-form-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading uppercase">Parameters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Material */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide">Material</Label>
                <Select value={material} onValueChange={setMaterial}>
                  <SelectTrigger data-testid="material-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATERIALS.map((m) => (
                      <SelectItem key={m.name} value={m.name}>
                        {m.name} <span className="text-muted-foreground ml-1">({m.density} g/cm³)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* OD / ID / Thickness */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide">OD (mm)</Label>
                  <Input type="number" min={0} step={0.1} value={od || ''} onChange={handleNum(setOd)}
                    data-testid="input-od" className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide">ID (mm)</Label>
                  <Input type="number" min={0} step={0.1} value={id || ''} onChange={handleNum(setId)}
                    data-testid="input-id" className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide">T (mm)</Label>
                  <Input type="number" min={0} step={0.1} value={thickness || ''} onChange={handleNum(setThickness)}
                    data-testid="input-thickness" className="font-mono" />
                </div>
              </div>

              {/* Staggered Rows */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide">Staggered Rows</Label>
                  <Badge variant="secondary" className="font-mono" data-testid="rows-badge">{rows}</Badge>
                </div>
                <Slider
                  value={[rows]}
                  onValueChange={([v]) => setRows(v)}
                  min={1} max={10} step={1}
                  data-testid="rows-slider"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
                  <span>1</span><span>5</span><span>10</span>
                </div>
              </div>

              {/* Auto-calculated info */}
              {result && (
                <div className="pt-3 border-t space-y-2">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Auto-Calculated
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <span className="text-muted-foreground">Bridge web (w<sub>B</sub>)</span>
                    <span className="font-mono font-medium text-right">{result.wBridge.toFixed(2)} mm</span>
                    <span className="text-muted-foreground">Edge margin (w<sub>E</sub>)</span>
                    <span className="font-mono font-medium text-right">{result.wEdge.toFixed(2)} mm</span>
                    <span className="text-muted-foreground">Pitch (p)</span>
                    <span className="font-mono font-medium text-right">{result.p.toFixed(2)} mm</span>
                    <span className="text-muted-foreground">Row Height (h)</span>
                    <span className="font-mono font-medium text-right">{result.h.toFixed(2)} mm</span>
                    <span className="text-muted-foreground">Density</span>
                    <span className="font-mono font-medium text-right">{mat.density} g/cm³</span>
                    <span className="text-muted-foreground">Shear strength (&tau;<sub>s</sub>)</span>
                    <span className="font-mono font-medium text-right">{mat.shearStrength} MPa</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ────── Right: Results ────── */}
        <div className="lg:col-span-8 space-y-5">
          {/* Metrics Row */}
          {result ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="results-dashboard">
              <Metric
                label="Net Wt / 1000 pcs"
                value={result.netPer1000.toFixed(2)}
                unit="Kg"
                icon={Weight}
                border="border-l-blue-500"
                accent="text-blue-600"
              />
              <Metric
                label="Gross Wt / 1000 pcs"
                value={result.grossPer1000.toFixed(2)}
                unit="Kg"
                icon={Layers}
                border="border-l-amber-500"
                accent="text-amber-600"
              />
              <Metric
                label="Scrap"
                value={result.scrap.toFixed(1) + '%'}
                unit={rows > 1 ? 'Honeycomb layout' : 'Single row'}
                icon={Percent}
                border={result.scrap < 50 ? 'border-l-emerald-500' : 'border-l-red-500'}
                accent={result.scrap < 50 ? 'text-emerald-600' : 'text-red-600'}
              />
              <Metric
                label="Strip Width"
                value={result.stripWidth.toFixed(1)}
                unit="mm (optimum)"
                icon={Ruler}
                border="border-l-purple-500"
                accent="text-purple-600"
              />
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-10 text-center text-muted-foreground">
                <Circle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Enter valid dimensions (OD &gt; ID &gt; 0) to see results</p>
              </CardContent>
            </Card>
          )}

          {/* ────── Press Tonnage ────── */}
          {result && (
            <Card data-testid="press-tonnage-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-heading uppercase flex items-center gap-2">
                    <Hammer className="h-4 w-4 text-orange-600" />
                    Press Tonnage Required
                  </CardTitle>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    F = π·(OD+ID)·t·τs × 1.10
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Compound */}
                  <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="tonnage-compound">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-bold text-amber-900 uppercase tracking-wide">
                        Compound Tool
                      </p>
                      <Gauge className="h-4 w-4 text-amber-700" />
                    </div>
                    <p className="text-3xl font-extrabold font-mono text-amber-800">
                      {result.compoundTonnage.toFixed(2)}
                      <span className="text-sm font-semibold ml-1">tonne</span>
                    </p>
                    <p className="text-[11px] text-amber-900/70 mt-1">
                      {result.compoundForceKN.toFixed(1)} kN &middot; 1 piece/stroke (blank + pierce in one station)
                    </p>
                  </div>

                  {/* Progressive */}
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="tonnage-progressive">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-bold text-emerald-900 uppercase tracking-wide">
                        Progressive Tool
                      </p>
                      <Gauge className="h-4 w-4 text-emerald-700" />
                    </div>
                    <p className="text-3xl font-extrabold font-mono text-emerald-800">
                      {result.progressiveTonnage.toFixed(2)}
                      <span className="text-sm font-semibold ml-1">tonne</span>
                    </p>
                    <p className="text-[11px] text-emerald-900/70 mt-1">
                      {result.progressiveForceKN.toFixed(1)} kN &middot; {rows} piece{rows>1?'s':''}/stroke (multi-row die)
                    </p>
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
                  Cut perimeter per washer = π·(OD + ID) = {result.perimeter.toFixed(1)} mm. Shear force scales linearly with sheet thickness & shear strength ({mat.shearStrength} MPa for {material}). Includes 10% stripping force allowance. Select a press ~25% above the higher value for safety margin.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Visualizer */}
          <Card data-testid="visualizer-card">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading uppercase">
                  Staggered Strip Layout
                </CardTitle>
                {result && (
                  <Badge variant="outline" className="font-mono text-xs">
                    {rows} row{rows > 1 ? 's' : ''} &middot; sin(60°) nesting
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="bg-muted/30 rounded-lg p-3 border" ref={vizRef}>
                  <StripVisualizer
                    od={od} id={id}
                    p={result.p} h={result.h} wEdge={result.wEdge}
                    rows={rows}
                    stripWidth={result.stripWidth}
                    materialColor={mat.color}
                  />
                  <div className="flex items-center justify-center gap-6 mt-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-full border-2 border-blue-600 bg-white" />
                      Washer blank (OD)
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-full border border-dashed border-slate-500" style={{ backgroundColor: mat.color, opacity: 0.25 }} />
                      Inner hole (ID)
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: mat.color, opacity: 0.18 }} />
                      Strip ({material})
                    </span>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
                  Visualizer will appear when valid dimensions are entered
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
